import {
  DriverType,
  ExcludedDriverType,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import React, { FC, useContext } from 'react';
import { CardSectionInfoPairs } from 'Shared/components';
import { formatDateShort } from 'Shared/utils';
import { Container, Pill } from '@clearcover/atlas';
import { STATE_OPTIONS, VALID_US_STATES_TYPE } from 'Shared/constants/states';
import styles from './styles.module.css';
import { formatDiff } from 'Policies/components/PolicyActivities/Common/Common';
import { InfoValuePairDataType } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';
import { PolicyContext } from 'Policies/utils';
import { env } from 'Shared/constants/env';

type DriverDetailsDiff = {
  diff: VersionedObjectDiff<DriverType | ExcludedDriverType>;
};
type CoveredDriverDetailsDiff = {
  oldDriver?: DriverType;
  newDriver: DriverType;
};
type ExcludedDriverDetailsDiff = {
  oldDriver?: ExcludedDriverType;
  newDriver: ExcludedDriverType;
};
type HeaderAndData = {
  headers: string[];
  data: InfoValuePairDataType[];
};

// Helper to convert boolean/truthy values to Yes/No strings
const formatBooleanToYesNo = (
  value: boolean | string | undefined | null
): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return value ? 'Yes' : 'No';
};

// Exposure details configuration - maps config flags to field keys and labels
// Defined outside component since it's static
const EXPOSURE_DETAILS_CONFIG = [
  {
    configKey: 'askLivesInHousehold',
    fieldKey: 'livesWithPolicyholder',
    label: 'Lives in primary household',
  },
  {
    configKey: 'askHasAccessToVehicles',
    fieldKey: 'hasAccessToVehicles',
    label:
      'Regularly drives, or has regular access to any of the insured vehicles',
  },
  {
    configKey: 'askHasOwnInsurancePolicy',
    fieldKey: 'hasOwnAutoPolicy',
    label: 'Has their own insurance policy with Clearcover or another carrier',
  },
] as const;

export const PolicyChangeDriverDetails: FC<DriverDetailsDiff> = ({ diff }) => {
  /*this may be confusing, here the reason:
    driver to show default will be the plain static values to show.
    When diff type is UPDATED we want to show the diff mode (oldValue -> newValue)
    so the driver to diff against object is there to control when that should show*/
  const driverToShowDefault =
    diff.type === 'ADDED' || diff.type === 'UPDATED' ? diff.new : diff.old;
  const driverToDiffAgainst =
    diff.type === 'UPDATED' ? diff.old : driverToShowDefault;
  if (isCoveredDriver(driverToShowDefault)) {
    return (
      <PolicyChangeCoveredDriverDetails
        newDriver={driverToShowDefault as DriverType}
        oldDriver={driverToDiffAgainst as DriverType}
      />
    );
  }

  return (
    <PolicyChangeExcludedDriverDetails
      newDriver={driverToShowDefault}
      oldDriver={driverToDiffAgainst as ExcludedDriverType}
    />
  );
};

const extractHeadersAndValues = (
  propertiesNames: string[],
  newObject: any,
  oldObject?: any
): HeaderAndData => {
  const headers = propertiesNames.map(propName => newObject[propName].title);
  const data = propertiesNames.map(propName =>
    formatDiff(
      oldObject ? oldObject[propName].value : null,
      newObject[propName].value
    )
  );
  return { headers, data };
};

const PolicyChangeCoveredDriverDetails: FC<CoveredDriverDetailsDiff> = ({
  oldDriver,
  newDriver,
}) => {
  const {
    driverOptions,
    details: { mailingState, showStudentSection },
  } = useContext(PolicyContext);

  const driverOptionsQuestionsConfig =
    driverOptions?.driverOptionsQuestionsConfig;

  // Build exposure details headers and data based on config
  const getExposureDetailsHeadersAndData = (): HeaderAndData => {
    if (!env.driverNewQuestionsEnabled || !driverOptionsQuestionsConfig) {
      return { headers: [], data: [] };
    }

    const headers: string[] = [];
    const data: InfoValuePairDataType[] = [];

    EXPOSURE_DETAILS_CONFIG.forEach(({ configKey, fieldKey, label }) => {
      if (
        driverOptionsQuestionsConfig[
          configKey as keyof typeof driverOptionsQuestionsConfig
        ]
      ) {
        headers.push(label);
        const newValue = newDriver.exposureDetails?.[fieldKey];
        const oldValue = oldDriver?.exposureDetails?.[fieldKey];
        data.push(
          formatDiff(
            formatBooleanToYesNo(oldValue),
            formatBooleanToYesNo(newValue)
          )
        );
      }
    });

    return { headers, data };
  };

  const exposureDetailsHeadersAndData = getExposureDetailsHeadersAndData();

  const residencyDetailsMap = {
    inStateResidencyDuration: `In a typical year, does this driver reside in ${mailingState} for at least 10 months?`,
    isOutOfStateStudent: 'Full-time student away at school',
    isStateResidentWithOutOfStateHomes: `Permanent resident of ${mailingState} with multiple households in multiple states`,
    hasVehicleWhileOutOfStateStudent: `Has possession of an insured vehicle under this policy while attending school outside of ${mailingState}`,
  };

  const residencyDetailsToInfoPair = (
    residencyDetails: Record<string, string | boolean | undefined>
  ) => {
    return Object.entries(residencyDetails).reduce((acc, [key, value]) => {
      const title =
        residencyDetailsMap[key as keyof typeof residencyDetailsMap];
      if (title) {
        // Only include if we have a valid title mapping
        acc[key] = {
          title,
          value: formatBooleanToYesNo(value) ?? 'No',
          dataValue: value,
        };
      }
      return acc;
    }, {} as Record<string, { title: string; value: string; dataValue: string | boolean | undefined }>);
  };

  // Filter out attributes based on config and duplicates
  const {
    militaryRank: _militaryRank,
    isDeployed: _isDeployed,
    // Conditionally exclude educationLevel and occupation based on config
    ...remainingAttributes
  } = newDriver.attributes;

  // Build filtered attributes based on config flags
  const askForEducation = driverOptionsQuestionsConfig?.askForEducation ?? true;
  const askForOccupation =
    driverOptionsQuestionsConfig?.askForOccupation ?? true;

  const driverAttributes = Object.fromEntries(
    Object.entries(remainingAttributes).filter(([key]) => {
      if (key === 'educationLevel' && !askForEducation) return false;
      if (key === 'occupation' && !askForOccupation) return false;
      return true;
    })
  );

  let driverAttributesNames = Object.getOwnPropertyNames(driverAttributes);

  const askStateResidencyDetails =
    driverOptionsQuestionsConfig?.askStateResidencyDetails;
  const stateResidencyDetailsAttributes = residencyDetailsToInfoPair(
    newDriver.residencyDetails ?? {}
  );

  if (askStateResidencyDetails && env.driverNewQuestionsEnabled) {
    driverAttributesNames = driverAttributesNames.concat(
      Object.getOwnPropertyNames(stateResidencyDetailsAttributes)
    );
  }

  // Filter old driver attributes the same way
  const filterAttributes = (
    attributes: Record<string, any> | undefined
  ): Record<string, any> => {
    if (!attributes) return {};
    return Object.fromEntries(
      Object.entries(attributes).filter(([key]) => {
        if (key === 'militaryRank' || key === 'isDeployed') return false;
        if (key === 'educationLevel' && !askForEducation) return false;
        if (key === 'occupation' && !askForOccupation) return false;
        return true;
      })
    );
  };

  const newDriverAttributes = {
    ...filterAttributes(newDriver.attributes),
    ...(askStateResidencyDetails && env.driverNewQuestionsEnabled
      ? { ...stateResidencyDetailsAttributes }
      : {}),
  };
  const oldDriverAttributes = {
    ...filterAttributes(oldDriver?.attributes),
    ...(askStateResidencyDetails && env.driverNewQuestionsEnabled
      ? { ...residencyDetailsToInfoPair(oldDriver?.residencyDetails ?? {}) }
      : {}),
  };

  const driverHeadersAndValues = extractHeadersAndValues(
    driverAttributesNames,
    newDriverAttributes,
    oldDriverAttributes
  );

  const militaryPropertiesNames = Object.getOwnPropertyNames(
    newDriver.military
  );
  const militaryHeadersAndValues = extractHeadersAndValues(
    militaryPropertiesNames,
    newDriver.military,
    oldDriver?.military
  );

  const studentPropertiesNames = Object.getOwnPropertyNames(newDriver.student);
  const studentHeadersAndValues = extractHeadersAndValues(
    studentPropertiesNames,
    newDriver.student,
    oldDriver?.student
  );

  return (
    <div data-testid="covered-driver-details">
      <Container>
        <h4>Driver Information</h4>
        <CardSectionInfoPairs
          headers={[
            'First Name',
            'Middle Initial',
            'Last Name',
            'Suffix',
            'Gender',
            'Marital status',
            'Date of Birth',
            'Relation to Applicant',
            'Driver License Number',
            'Driver License Issued State',
            'Driver License Status',
            ...exposureDetailsHeadersAndData.headers,
          ]}
          data={[
            formatDiff(
              oldDriver?.person.name.firstName,
              newDriver.person.name.firstName
            ),
            formatDiff(
              oldDriver?.person.name.middleName,
              newDriver.person.name.middleName
            ),
            formatDiff(
              oldDriver?.person.name.lastName,
              newDriver.person.name.lastName
            ),
            formatDiff(oldDriver?.person.suffix, newDriver.person.suffix),
            formatDiff(oldDriver?.person.gender, newDriver.person.gender),
            formatDiff(
              oldDriver?.person.maritalStatus,
              newDriver.person.maritalStatus
            ),
            formatDiff(
              oldDriverDateOfBirth(oldDriver),
              formatDateShort(newDriver.person.dateOfBirth)
            ),
            formatDiff(
              oldDriver?.relationToApplicant,
              newDriver.relationToApplicant
            ),
            formatDiff(
              oldDriver?.driversLicense.number,
              newDriver.driversLicense.number
            ),
            formatDiff(
              STATE_OPTIONS[
                oldDriver?.driversLicense.state as VALID_US_STATES_TYPE
              ],
              STATE_OPTIONS[
                newDriver.driversLicense.state as VALID_US_STATES_TYPE
              ]
            ),
            formatDiff(
              oldDriver?.driversLicense.status,
              newDriver.driversLicense.status
            ),
            ...exposureDetailsHeadersAndData.data,
          ]}
          className={styles.details}
        />
      </Container>
      <Container>
        <h4>Driver Attributes</h4>
        <CardSectionInfoPairs
          headers={driverHeadersAndValues.headers.concat('Age First Licensed')}
          data={driverHeadersAndValues.data.concat(
            formatDiff(oldDriver?.ageFirstLicensed, newDriver.ageFirstLicensed)
          )}
          className={styles.details}
        />
      </Container>
      {newDriver.military && (
        <Container>
          <h4>Military Info</h4>
          <CardSectionInfoPairs
            headers={militaryHeadersAndValues.headers}
            data={militaryHeadersAndValues.data}
            className={styles.details}
          />
        </Container>
      )}
      {newDriver.student && showStudentSection && (
        <Container>
          <h4>Student</h4>
          <CardSectionInfoPairs
            headers={studentHeadersAndValues.headers}
            data={studentHeadersAndValues.data}
            className={styles.details}
          />
        </Container>
      )}

      {/* TODO out of scope
      <h4 caption="Assigned Vehicles" id="assigned-vehicles">
      </h4>
      <h4 caption="Prior carrier" id="prior-carrier">
      </h4>
      <h4 caption="SR-22" id="sr22s">
      </h4>
      <h4 caption="Violations" id="violations">
      </h4>
      <h4 caption="Accidents" id="accidents" lastSection={true}>
      </h4> */}
    </div>
  );
};

const oldDriverDateOfBirth = (
  oldDriver: DriverType | ExcludedDriverType | undefined
) =>
  oldDriver?.person.dateOfBirth
    ? formatDateShort(oldDriver?.person.dateOfBirth)
    : '';

const PolicyChangeExcludedDriverDetails: FC<ExcludedDriverDetailsDiff> = ({
  newDriver,
  oldDriver,
}) => {
  const { driverOptions } = useContext(PolicyContext);

  const excludedDriverOptionsQuestionsConfig =
    driverOptions?.excludedDriverOptionsQuestionsConfig;

  // Build headers and data dynamically based on config
  const headers: string[] = [
    'First Name',
    'Middle Initial',
    'Last Name',
    'Date of Birth',
  ];
  const data: InfoValuePairDataType[] = [
    formatDiff(
      oldDriver?.person.name.firstName,
      newDriver.person.name.firstName
    ),
    formatDiff(
      oldDriver?.person.name.middleName,
      newDriver.person.name.middleName
    ),
    formatDiff(oldDriver?.person.name.lastName, newDriver.person.name.lastName),
    formatDiff(
      oldDriverDateOfBirth(oldDriver),
      formatDateShort(newDriver.person.dateOfBirth)
    ),
  ];

  // Add Gender if configured
  if (excludedDriverOptionsQuestionsConfig?.askForGender) {
    headers.push('Gender');
    data.push(formatDiff(oldDriver?.person.gender, newDriver.person.gender));
  }

  // Add Marital Status if configured
  if (excludedDriverOptionsQuestionsConfig?.askForMaritalStatus) {
    headers.push('Marital Status');
    data.push(
      formatDiff(
        oldDriver?.person.maritalStatus,
        newDriver.person.maritalStatus
      )
    );
  }

  return (
    <div data-testid="excluded-driver-details">
      <Container>
        <h4>
          Driver Information{' '}
          <Pill variant="disabled" className={styles.pill}>
            Excluded
          </Pill>
        </h4>
        <CardSectionInfoPairs
          headers={headers}
          data={data}
          className={styles.details}
        />
      </Container>
    </div>
  );
};

function isCoveredDriver(
  driver: DriverType | ExcludedDriverType
): driver is DriverType {
  return 'driversLicense' in driver;
}
